import React, { useState } from 'react';
import AchievementCard from './AchievementCard';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const AchievementsDisplay = ({ achievements, calculatedDays, language }) => {
  // Filter tab states
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Check if calculatedDays is valid
  const hasValidDays = typeof calculatedDays === 'number' && !isNaN(calculatedDays) && calculatedDays >= 0;
  
  // Sort achievements by days (ascending for display, will be reversed for unlocked)
  const sortedAchievements = [...achievements].sort((a, b) => a.days - b.days);
  
  // Define filter options - Always visible regardless of date selection
  const filterOptions = [
    { id: 'all', labelZh: '全部', labelEn: 'All', icon: FiIcons.FiGrid },
    { id: 'unlocked', labelZh: '已達成', labelEn: 'Achieved', icon: FiIcons.FiCheckCircle },
    { id: 'upcoming', labelZh: '即將到來', labelEn: 'Upcoming', icon: FiIcons.FiClock },
    { id: 'special', labelZh: '特殊紀念', labelEn: 'Special', icon: FiIcons.FiStar }
  ];
  
  // Filter achievements based on active filter
  const unlockedAchievements = hasValidDays 
    ? sortedAchievements.filter(a => calculatedDays >= a.days).sort((a, b) => b.days - a.days) // Sort from recent to distant
    : [];
    
  const lockedAchievements = hasValidDays 
    ? sortedAchievements.filter(a => calculatedDays < a.days)
    : sortedAchievements;
    
  const specialAchievements = sortedAchievements.filter(a => a.special);
  
  let filteredAchievements = [];
  switch (activeFilter) {
    case 'unlocked':
      filteredAchievements = unlockedAchievements;
      break;
    case 'upcoming':
      filteredAchievements = lockedAchievements;
      break;
    case 'special':
      // For special achievements, we want to keep the unlocked ones at the top and sorted from recent to distant
      if (hasValidDays) {
        const unlockedSpecial = specialAchievements.filter(a => calculatedDays >= a.days).sort((a, b) => b.days - a.days);
        const lockedSpecial = specialAchievements.filter(a => calculatedDays < a.days);
        filteredAchievements = [...unlockedSpecial, ...lockedSpecial];
      } else {
        filteredAchievements = specialAchievements;
      }
      break;
    default:
      // For "all" filter, show unlocked achievements at the top sorted from recent to distant
      if (hasValidDays) {
        filteredAchievements = [...unlockedAchievements, ...lockedAchievements];
      } else {
        filteredAchievements = sortedAchievements;
      }
  }
  
  // Container variants for staggered animation
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  // Item variants for animation
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <>
      {hasValidDays && unlockedAchievements.length > 0 ? (
        <div className="mb-4 text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-love-100 dark:bg-love-900/30 text-love-600 dark:text-love-300 shadow-sm">
            <span className="text-lg mr-2">🏆</span>
            <span className="font-medium">
              {language === 'zh' 
                ? `已解鎖 ${unlockedAchievements.length} 個成就！` 
                : `Unlocked ${unlockedAchievements.length} achievements!`}
            </span>
          </div>
        </div>
      ) : hasValidDays ? (
        <div className="mb-4 text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
            <span className="text-lg mr-2">🏅</span>
            <span>
              {language === 'zh' 
                ? `距離第一個成就還有 ${sortedAchievements[0]?.days - calculatedDays} 天` 
                : `${sortedAchievements[0]?.days - calculatedDays} days until your first achievement`}
            </span>
          </div>
        </div>
      ) : null}
      
      {/* Filter Tabs - Always show regardless of date selection */}
      <div className="flex items-center justify-center mb-6 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg overflow-hidden overflow-x-auto">
        {filterOptions.map((option) => (
          <button
            key={option.id}
            onClick={() => setActiveFilter(option.id)}
            className={`flex items-center px-3 py-2 text-sm rounded-md transition-all ${
              activeFilter === option.id
                ? 'bg-white dark:bg-gray-700 text-love-600 dark:text-love-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-700/50'
            }`}
          >
            <SafeIcon icon={option.icon} className="mr-1" />
            <span>{language === 'zh' ? option.labelZh : option.labelEn}</span>
            {option.id === 'unlocked' && unlockedAchievements.length > 0 && (
              <span className="ml-1 text-xs bg-love-100 dark:bg-love-900 text-love-600 dark:text-love-300 px-1.5 py-0.5 rounded-full">
                {unlockedAchievements.length}
              </span>
            )}
            {option.id === 'special' && specialAchievements.length > 0 && (
              <span className="ml-1 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300 px-1.5 py-0.5 rounded-full">
                {specialAchievements.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div 
          key={activeFilter}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          variants={container}
          initial="hidden"
          animate="show"
          exit={{ opacity: 0 }}
        >
          {filteredAchievements.length > 0 ? (
            filteredAchievements.map((achievement) => (
              <motion.div key={achievement.id} variants={item}>
                <AchievementCard 
                  achievement={achievement} 
                  isUnlocked={hasValidDays && calculatedDays >= achievement.days} 
                  language={language} 
                />
              </motion.div>
            ))
          ) : (
            <motion.div 
              className="col-span-1 sm:col-span-2 text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg"
              variants={item}
            >
              <p className="text-gray-500 dark:text-gray-400">
                {language === 'zh' ? '沒有符合此篩選條件的成就' : 'No achievements match this filter'}
              </p>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default AchievementsDisplay;